import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, TrendingUp, Calendar, Weight, Target, BarChart3, ArrowRight, Layers, Repeat } from 'lucide-react';
import { Exercise, Client, ExerciseLog } from '../types';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
// import WeightProgressionChart from './WeightProgressionChart'; // временно убран

interface ExerciseStatsModalProps {
  exercise: Exercise | null;
  client: Client | null;
  exerciseLogs: ExerciseLog[];
  isOpen: boolean;
  onClose: () => void;
  isLoading?: boolean;
}

interface ExerciseStats {
  totalWorkouts: number;
  totalSets: number;
  totalReps: number;
  avgWeight: number;
  maxWeight: number;
  minWeight: number;
  weightProgression: Array<{
    date: string;
    weight: number;
    workoutName: string;
  }>;
}

const ExerciseStatsModal: React.FC<ExerciseStatsModalProps> = ({
  exercise,
  client,
  exerciseLogs,
  isOpen,
  onClose,
  isLoading = false
}) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<ExerciseStats | null>(null);

  useEffect(() => {
    if (exercise && client) {
      calculateStats();
    }
  }, [exercise, client, exerciseLogs]);

  const calculateStats = () => {
    if (!exercise || !client) {
      return;
    }

    // Фильтруем логи только для этого упражнения и клиента
    const relevantLogs = exerciseLogs.filter(log => 
      log.workout_exercise?.exercise_id === exercise.id
    );
    
    if (relevantLogs.length === 0) {
      setStats(null);
      return;
    }

    // Собираем данные о весах
    const weights = relevantLogs
      .map(log => log.actual_weight)
      .filter(weight => weight !== null && weight !== undefined) as number[];

    // Создаем прогрессию веса
    const weightProgression = relevantLogs
      .filter(log => log.actual_weight && log.actual_weight > 0)
      .map(log => ({
        date: log.completed_at || log.created_at,
        weight: log.actual_weight!,
        workoutName: log.workout_exercise?.workout?.name || 'Тренировка'
      }))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    const calculatedStats: ExerciseStats = {
      totalWorkouts: relevantLogs.length,
      totalSets: relevantLogs.reduce((sum, log) => sum + log.completed_sets, 0),
      totalReps: relevantLogs.reduce((sum, log) => sum + log.completed_reps, 0),
      avgWeight: weights.length > 0 ? weights.reduce((sum, w) => sum + w, 0) / weights.length : 0,
      maxWeight: weights.length > 0 ? Math.max(...weights) : 0,
      minWeight: weights.length > 0 ? Math.min(...weights) : 0,
      weightProgression
    };

    setStats(calculatedStats);
  };

  const formatWeight = (weight: number) => {
    return `${weight.toFixed(1)} кг`;
  };

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), 'dd MMM yyyy', { locale: ru });
  };

  if (!isOpen || !exercise || !client) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl">
          <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
            <div className="flex-1">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
                Статистика упражнения
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {exercise.name} • {client.first_name} {client.last_name}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
            >
              <X size={24} />
            </button>
          </div>
          
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-lg text-gray-700">Загрузка статистики...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl">
          <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
            <div className="flex-1">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
                Статистика упражнения
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {exercise.name} • {client.first_name} {client.last_name}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
            >
              <X size={24} />
            </button>
          </div>
          
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <BarChart3 className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Нет данных</h3>
              <p className="text-gray-500">Для этого упражнения пока нет статистики</p>
              <button
                onClick={onClose}
                className="mt-4 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
              >
                Закрыть
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
          <div className="flex-1">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
              Статистика упражнения
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {exercise.name} • {client.first_name} {client.last_name}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-4 sm:p-6 space-y-6">
          {/* Основная статистика */}
          <div className="flex flex-row gap-2 sm:gap-4">
            <div className="bg-blue-50 rounded-lg p-2 sm:p-4 text-center flex-1">
              <div className="text-lg sm:text-2xl font-bold text-blue-600">{stats.totalWorkouts}</div>
              <div className="text-xs sm:text-sm text-blue-700">Тренировок</div>
            </div>
            <div className="bg-green-50 rounded-lg p-2 sm:p-4 text-center flex-1">
              <div className="text-lg sm:text-2xl font-bold text-green-600">{stats.totalSets}</div>
              <div className="text-xs sm:text-sm text-green-700">Подходов</div>
            </div>
            <div className="bg-purple-50 rounded-lg p-2 sm:p-4 text-center flex-1">
              <div className="text-lg sm:text-2xl font-bold text-purple-600">{stats.totalReps}</div>
              <div className="text-xs sm:text-sm text-purple-700">Повторений</div>
            </div>
          </div>

          {/* Статистика по весам */}
          {stats.maxWeight > 0 && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Weight className="mr-2 h-5 w-5 text-gray-600" />
                Статистика по весам
              </h3>
              <div className="flex flex-row gap-2 sm:gap-4">
                <div className="text-center flex-1">
                  <div className="text-lg sm:text-xl font-semibold text-orange-600">{formatWeight(stats.minWeight)}</div>
                  <div className="text-xs sm:text-sm text-gray-600">Минимальный вес</div>
                </div>
                <div className="text-center flex-1">
                  <div className="text-lg sm:text-xl font-semibold text-green-600">{formatWeight(stats.maxWeight)}</div>
                  <div className="text-xs sm:text-sm text-gray-600">Максимальный вес</div>
                </div>
              </div>
            </div>
          )}

          {/* График прогрессии веса - временно убран */}
          {/* 
          {stats.weightProgression.length > 1 && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <TrendingUp className="mr-2 h-5 w-5 text-gray-600" />
                Прогрессия веса
              </h3>
              <WeightProgressionChart data={stats.weightProgression} />
            </div>
          )}
          */}

          {/* История тренировок */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Target className="mr-2 h-5 w-5 text-gray-600" />
              История выполнения
            </h3>
            <div className="space-y-2">
              {exerciseLogs
                .filter(log => log.workout_exercise?.exercise_id === exercise.id)
                .sort((a, b) => {
                  const dateA = a.workout_exercise?.workout?.date || a.completed_at || a.created_at;
                  const dateB = b.workout_exercise?.workout?.date || b.completed_at || b.created_at;
                  return new Date(dateB).getTime() - new Date(dateA).getTime();
                })
                .map((log, index) => (
                  <div key={index} className="bg-white rounded-lg p-2 shadow-sm">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900">
                          {log.workout_exercise?.workout?.name || 'Тренировка'}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {log.workout_exercise?.workout?.date ? formatDate(log.workout_exercise.workout.date) : formatDate(log.created_at)}
                        </div>
                        <div className="flex items-center space-x-3 mt-1 text-xs text-gray-600">
                          <div className="flex items-center">
                            <Layers size={12} className="text-gray-500 mr-1" />
                            <span>{log.completed_sets}</span>
                          </div>
                          <div className="flex items-center">
                            <Repeat size={12} className="text-gray-500 mr-1" />
                            <span>{log.completed_reps}</span>
                          </div>
                          {(log.actual_weight !== null && log.actual_weight !== undefined) || (log.workout_exercise?.weight !== null && log.workout_exercise?.weight !== undefined) ? (
                            <div className="flex items-center">
                              <Weight size={12} className="text-gray-500 mr-1" />
                              <span>{log.actual_weight !== null && log.actual_weight !== undefined ? log.actual_weight : log.workout_exercise?.weight} кг</span>
                            </div>
                          ) : null}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 ml-3">
                        {log.workout_exercise?.workout && (
                          <button
                            onClick={() => {
                              onClose();
                              navigate(`/workouts/${log.workout_exercise!.workout!.id}`);
                            }}
                            className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                            title="Перейти к тренировке"
                          >
                            <ArrowRight size={14} />
                          </button>
                        )}
                      </div>
                    </div>
                    {log.notes && (
                      <div className="mt-2 text-xs text-gray-600 bg-gray-50 rounded p-2">
                        {log.notes}
                      </div>
                    )}
                  </div>
                ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExerciseStatsModal; 